import React, { useEffect, useState, useRef } from 'react';
import { Modal, Button, Row, Col, Table } from 'react-bootstrap';
import { FaPrint, FaDownload, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import StudentService from '../../services/studentService';

const StudentReceiptView = ({ show, onHide, transaction }) => {
  const [receiptData, setReceiptData] = useState(null);
  const [loading, setLoading] = useState(false);
  const receiptRef = useRef();

  useEffect(() => {
    if (show && transaction) {
      loadReceipt();
    }
  }, [show, transaction]);

  const loadReceipt = async () => {
    try {
      setLoading(true);
      const response = await StudentService.getReceipt(transaction.id);
      setReceiptData(response.data);
    } catch (error) {
      toast.error('Failed to load receipt');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!receiptRef.current) return;
    
    try {
      toast.info('Generating PDF...');
      
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true,
        useCORS: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [canvas.width * 0.75, canvas.height * 0.75]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width * 0.75, canvas.height * 0.75);
      pdf.save(`Receipt-${receiptData?.receipt_number || 'receipt'}.pdf`);
      
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    let words = '';
    if (num >= 100000) {
      words += convertLessThanThousand(Math.floor(num / 100000)) + ' Lakh ';
      num %= 100000;
    }
    if (num >= 1000) {
      words += convertLessThanThousand(Math.floor(num / 1000)) + ' Thousand ';
      num %= 1000;
    }
    if (num > 0) {
      words += convertLessThanThousand(num);
    }
    return words.trim() + ' Only';
  };

  // Calculate balance due (Total Course Fee - Amount Paid)
  const calculateBalanceDue = () => {
    if (!receiptData) return 0;
    const courseFee = receiptData.course_fee || receiptData.total_fees || 0;
    const paidAmount = receiptData.amount || 0;
    return courseFee - paidAmount;
  };

  if (!receiptData && !loading) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaPrint className="me-2" />
          Payment Receipt
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Receipt for printing/downloading */}
            <div ref={receiptRef} className="p-4" style={{ backgroundColor: 'white', fontFamily: 'Arial, sans-serif' }}>
              {/* Institute Header */}
              <div className="text-center mb-4" style={{ borderBottom: '2px solid #333', paddingBottom: '15px' }}>
                <h2 className="mb-1" style={{ color: '#2c3e50' }}>{receiptData?.institute?.name || 'Institute LMS'}</h2>
                <p className="mb-1">{receiptData?.institute?.address || '123 Education Street, Knowledge City'}</p>
                <p className="mb-3">
                  Tel: {receiptData?.institute?.phone || '+91 9876543210'} | 
                  Email: {receiptData?.institute?.email || 'accounts@institutelms.com'}
                </p>
                <h4 className="py-2" style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6' }}>
                  PAYMENT RECEIPT
                </h4>
              </div>

              {/* Receipt Details */}
              <Row className="mb-4">
                <Col xs={6}>
                  <p><strong>Receipt No:</strong> {receiptData?.receipt_number}</p>
                  <p><strong>Transaction ID:</strong> {receiptData?.transaction_id}</p>
                </Col>
                <Col xs={6} className="text-end">
                  <p><strong>Date:</strong> {formatDate(receiptData?.payment_date)}</p>
                  <p><strong>Payment Mode:</strong> <span className="text-uppercase">{receiptData?.payment_method}</span></p>
                </Col>
              </Row>

              {/* Student Details */}
              <div className="mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                <h5>Student Information</h5>
                <Row>
                  <Col xs={6}>
                    <p className="mb-1"><strong>Name:</strong> {receiptData?.student_name}</p>
                    <p className="mb-1"><strong>Email:</strong> {receiptData?.email}</p>
                  </Col>
                  <Col xs={6}>
                    <p className="mb-1"><strong>Phone:</strong> {receiptData?.phone}</p>
                  </Col>
                </Row>
              </div>

              {/* Payment Details - CORRECTED TABLE */}
              <Table bordered className="mb-4">
                <thead style={{ backgroundColor: '#e9ecef' }}>
                  <tr>
                    <th>Description</th>
                    <th className="text-end">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Row 1: Course Fee */}
                  <tr>
                    <td>
                      <strong>Course Fee</strong>
                      <br />
                      <small className="text-muted">{receiptData?.course_name}</small>
                    </td>
                    <td className="text-end fw-bold">
                      {formatCurrency(receiptData?.course_fee || receiptData?.total_fees || 0)}
                    </td>
                  </tr>
                  
                  {/* Row 2: Payment Received */}
                  <tr>
                    <td>
                      <strong>Payment Received</strong>
                      <br />
                      <small className="text-muted">{receiptData?.remarks || 'Partial payment'}</small>
                    </td>
                    <td className="text-end fw-bold text-success">
                      {formatCurrency(receiptData?.amount)}
                    </td>
                  </tr>
                  
                  {/* Row 3: Balance Due */}
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td className="text-end"><strong>Balance Due:</strong></td>
                    <td className="text-end fw-bold text-danger">
                      {formatCurrency(calculateBalanceDue())}
                    </td>
                  </tr>
                </tbody>
              </Table>

              {/* Amount in Words (for paid amount) */}
              <div className="mb-4 p-2" style={{ borderTop: '1px dashed #dee2e6', borderBottom: '1px dashed #dee2e6' }}>
                <p className="mb-0">
                  <strong>Amount Paid in Words:</strong>{' '}
                  {receiptData?.amount && numberToWords(Math.round(receiptData.amount))}
                </p>
              </div>

              {/* Footer */}
              <Row className="mt-5">
                <Col xs={6}>
                  <p>Received by: ___________________</p>
                  <p className="mt-4">(Authorized Signatory)</p>
                </Col>
                <Col xs={6} className="text-end">
                  <p>For {receiptData?.institute?.name || 'Institute LMS'}</p>
                  <p className="mt-4">(Institute Stamp)</p>
                </Col>
              </Row>

              <p className="text-center text-muted mt-4 small" style={{ borderTop: '1px solid #dee2e6', paddingTop: '10px' }}>
                This is a computer generated receipt - valid without signature
              </p>
            </div>
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          <FaTimes className="me-2" /> Close
        </Button>
        <Button 
          variant="success" 
          onClick={handleDownloadPDF} 
          disabled={loading || !receiptData}
        >
          <FaDownload className="me-2" /> Download PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StudentReceiptView;